'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { Plane, Clock, ArrowRight, Tag } from 'lucide-react';

interface AirlineRoute {
  id: string;
  from: string;
  fromCode: string;
  to: string;
  toCode: string;
  image: string;
  alt: string;
  price: number;
  duration: string;
  airline: string;
  airlineCode: string;
  flightsPerWeek: number;
  tag?: string;
  span?: string;
}

const AIRLINE_ROUTES: AirlineRoute[] = [
{
  id: 'route-alg-par',
  from: 'Algiers',
  fromCode: 'ALG',
  to: 'Paris',
  toCode: 'CDG',
  image: "https://images.unsplash.com/photo-1653554604072-3cc639952e57",
  alt: 'Eiffel Tower and Paris skyline at dusk with golden lights reflecting on the Seine river',
  price: 28500,
  duration: '2h 45m',
  airline: 'Air Algérie',
  airlineCode: 'AH',
  flightsPerWeek: 14,
  tag: 'Most Popular',
  span: 'col-span-2 row-span-2'
},
{
  id: 'route-alg-lon',
  from: 'Algiers',
  fromCode: 'ALG',
  to: 'London',
  toCode: 'LHR',
  image: "https://images.unsplash.com/photo-1485727001277-7800c9dafcaa",
  alt: 'Tower Bridge in London spanning the Thames river on a clear blue sky day',
  price: 34200,
  duration: '3h 10m',
  airline: 'Air Algérie',
  airlineCode: 'AH',
  flightsPerWeek: 7,
  tag: 'New Route'
},
{
  id: 'route-alg-istan',
  from: 'Algiers',
  fromCode: 'ALG',
  to: 'Istanbul',
  toCode: 'IST',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1a8c629-1772992682770.png",
  alt: 'Blue Mosque and Hagia Sophia dominating the Istanbul skyline at sunset over the Bosphorus',
  price: 22800,
  duration: '3h 30m',
  airline: 'Air Algérie',
  airlineCode: 'AH',
  flightsPerWeek: 10
},
{
  id: 'route-alg-dubai',
  from: 'Algiers',
  fromCode: 'ALG',
  to: 'Dubai',
  toCode: 'DXB',
  image: "https://images.unsplash.com/photo-1617559057121-5ccad3b7571b",
  alt: 'Burj Khalifa and Dubai skyline glittering at night with city lights reflecting on the water',
  price: 41500,
  duration: '6h 20m',
  airline: 'Air Algérie',
  airlineCode: 'AH',
  flightsPerWeek: 5,
  tag: 'Best Deal',
  span: 'col-span-2'
},
{
  id: 'route-alg-cairo',
  from: 'Algiers',
  fromCode: 'ALG',
  to: 'Cairo',
  toCode: 'CAI',
  image: "https://images.unsplash.com/photo-1666134620553-84fea5aaf1dc",
  alt: 'Great Pyramids of Giza rising from the desert sand under a clear blue sky near Cairo',
  price: 19900,
  duration: '2h 55m',
  airline: 'Air Algérie',
  airlineCode: 'AH',
  flightsPerWeek: 7
}];


export default function AirlineDestinations() {
  const router = useRouter();

  const handleRouteClick = (route: AirlineRoute) => {
    const params = new URLSearchParams({
      from: route.fromCode,
      to: route.toCode,
      fromCity: route.from,
      toCity: route.to
    });
    router.push(`/flights?${params.toString()}`);
  };

  return (
    <section className="py-10 sm:py-14" style={{ background: '#F0F4FA' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-5 sm:mb-8 gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg gradient-primary flex items-center justify-center">
                <Plane size={14} className="text-white" />
              </div>
              <p className="text-xs font-display font-600 text-accent-500 uppercase tracking-widest">
                Air Algérie
              </p>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-800 text-gray-900" style={{ fontWeight: 800 }}>
              Fly with Algerian Airlines
            </h2>
            <p className="text-sm text-gray-500 font-body mt-1">
              Browse direct routes, compare prices and book your next flight
            </p>
          </div>
          <button
            onClick={() => router.push('/flights')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-display font-600 hover:bg-primary-600 active:scale-95 transition-all duration-150 shadow-sm self-start sm:self-auto"
            style={{ fontWeight: 600 }}>
            All Flights
            <ArrowRight size={15} />
          </button>
        </div>

        {/* Mobile: horizontal scroll cards */}
        <div className="sm:hidden flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          {AIRLINE_ROUTES.map((route) => (
            <div
              key={route.id}
              onClick={() => handleRouteClick(route)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleRouteClick(route)}
              aria-label={`Fly from ${route.from} to ${route.to} from ${route.price.toLocaleString()} DZD`}
              className="group relative rounded-2xl overflow-hidden cursor-pointer flex-shrink-0 w-56 h-48 active:scale-95 transition-transform duration-150">
              <img
                src={route.image}
                alt={route.alt}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              {route.tag && (
                <div className="absolute top-3 left-3">
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500 text-white text-xs font-display font-600" style={{ fontWeight: 600 }}>
                    <Tag size={9} />
                    {route.tag}
                  </span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="font-display font-700 text-white text-sm" style={{ fontWeight: 700 }}>{route.fromCode}</span>
                  <div className="flex items-center gap-1 flex-1">
                    <div className="h-px bg-white/40 flex-1" />
                    <Plane size={10} className="text-white/70 rotate-90" />
                    <div className="h-px bg-white/40 flex-1" />
                  </div>
                  <span className="font-display font-700 text-white text-sm" style={{ fontWeight: 700 }}>{route.toCode}</span>
                </div>
                <h3 className="font-display font-700 text-white text-base leading-tight mb-1" style={{ fontWeight: 700 }}>{route.to}</h3>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-white/75 text-xs font-body">
                    <Clock size={10} />{route.duration}
                  </span>
                  <span className="font-display font-700 text-white text-xs" style={{ fontWeight: 700 }}>
                    {route.price.toLocaleString()} DZD
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop bento grid */}
        <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 auto-rows-[200px]">
          {AIRLINE_ROUTES.map((route) => (
            <div
              key={route.id}
              onClick={() => handleRouteClick(route)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleRouteClick(route)}
              aria-label={`Fly from ${route.from} to ${route.to} from ${route.price.toLocaleString()} DZD`}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform duration-150 ${route.span ?? ''}`}
              style={{ minHeight: 200 }}>
              <img
                src={route.image}
                alt={route.alt}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              {route.tag && (
                <div className="absolute top-3 left-3">
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500 text-white text-xs font-display font-600"
                    style={{ fontWeight: 600 }}>
                    <Tag size={10} />
                    {route.tag}
                  </span>
                </div>
              )}
              <div className="absolute top-3 right-3">
                <span className="px-2 py-1 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-display font-600 border border-white/25"
                  style={{ fontWeight: 600 }}>
                  {route.airlineCode}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-display font-700 text-white text-base" style={{ fontWeight: 700 }}>{route.fromCode}</span>
                  <div className="flex items-center gap-1 flex-1">
                    <div className="h-px bg-white/40 flex-1" />
                    <Plane size={12} className="text-white/70 rotate-90" />
                    <div className="h-px bg-white/40 flex-1" />
                  </div>
                  <span className="font-display font-700 text-white text-base" style={{ fontWeight: 700 }}>{route.toCode}</span>
                </div>
                <h3 className="font-display font-700 text-white text-lg leading-tight mb-1" style={{ fontWeight: 700 }}>{route.to}</h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-white/75 text-xs font-body">
                      <Clock size={11} />{route.duration}
                    </span>
                    <span className="text-white/60 text-xs font-body">{route.flightsPerWeek}x/week</span>
                  </div>
                  <div className="text-right">
                    <span className="text-white/70 text-xs font-body">from </span>
                    <span className="font-display font-700 text-white text-sm" style={{ fontWeight: 700 }}>
                      {route.price.toLocaleString()} DZD
                    </span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-primary-500/0 group-hover:bg-primary-500/10 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center gap-2">
                  <Plane size={14} className="text-primary-500" />
                  <span className="text-sm font-display font-600 text-gray-900" style={{ fontWeight: 600 }}>Browse Flights</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contract CTA Banner */}
        <div className="mt-5 sm:mt-8 rounded-2xl overflow-hidden relative"
          style={{ background: 'linear-gradient(135deg, #0f2744 0%, #1a4a8a 60%, #1e5799 100%)' }}>
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="relative px-4 sm:px-6 py-5 sm:py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2.5 py-1 rounded-full bg-white/20 text-white text-xs font-display font-600 border border-white/25" style={{ fontWeight: 600 }}>
                  ✈️ Corporate Travel
                </span>
              </div>
              <h3 className="font-display font-800 text-white text-base sm:text-xl mb-1" style={{ fontWeight: 800 }}>
                Sign a Contract with Air Algérie
              </h3>
              <p className="text-white/70 text-sm font-body max-w-md">
                Get exclusive corporate rates, priority booking, and dedicated support for your business travel needs.
              </p>
            </div>
            <button
              onClick={() => router.push('/flights')}
              className="flex-shrink-0 flex items-center gap-2 px-5 sm:px-6 py-3 rounded-xl bg-white text-gray-900 text-sm font-display font-700 hover:bg-gray-100 active:scale-95 transition-all duration-150 shadow-sm w-full sm:w-auto justify-center"
              style={{ fontWeight: 700 }}>
              Get Started
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
